import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { useDateTimeFormat } from '../hooks/useDateTimeFormat';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Euro, Calendar, TrendingUp, TrendingDown, ArrowLeft, LogOut, FileImage, Eye, Upload, Loader2, Camera, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


export default function DriverExpenses() {
  const { token, user, logout } = useAuth();
  const { formatDateTime } = useDateTimeFormat();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryPeriod, setSummaryPeriod] = useState('month');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    date: '',
    reason: '',
    receipt_data: null,
    receipt_filename: null
  });

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [summaryPeriod]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/expenses/summary?period=${summaryPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        amount: formData.amount ? parseFloat(formData.amount) : null,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        reason: formData.reason || null,
        receipt_data: formData.receipt_data,
        receipt_filename: formData.receipt_filename
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Expense added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/expenses/${expenseToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense deleted successfully');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    const expenseDate = expense.date ? new Date(expense.date) : null;
    const localDateTime = expenseDate 
      ? new Date(expenseDate.getTime() - expenseDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      : '';
    
    setFormData({
      amount: expense.amount?.toString() || '',
      date: localDateTime,
      reason: expense.reason || '',
      receipt_data: expense.receipt_data || null,
      receipt_filename: expense.receipt_filename || null
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      date: '',
      reason: '',
      receipt_data: null,
      receipt_filename: null
    });
    setEditingExpense(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          receipt_data: reader.result,
          receipt_filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const viewReceipt = (expense) => {
    if (expense.receipt_data) {
      setSelectedReceipt(expense.receipt_data);
      setShowReceiptModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <header className="bg-white border-b border-blue-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/driver')}
              variant="ghost"
              size="icon"
              className="text-gray-700 hover:text-black hover:bg-blue-50"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-black">My Expenses</h1>
              <p className="text-sm text-gray-600 mt-1">Track your expenses and earnings</p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            className="text-gray-700 hover:text-black hover:bg-blue-50"
          >
            <LogOut className="mr-2 h-5 w-5" />
            Logout
          </Button>
        </div>
      </header>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Summary Section */}
        {summary && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium">Total Commission</p>
                  <p className="text-3xl font-bold mt-2">€{summary.total_commission?.toFixed(2) || '0.00'}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-emerald-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm font-medium">Total Expenses</p>
                  <p className="text-3xl font-bold mt-2">€{summary.total_expenses?.toFixed(2) || '0.00'}</p>
                </div>
                <TrendingDown className="h-12 w-12 text-red-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Net Total</p>
                  <p className="text-3xl font-bold mt-2">€{summary.total_net?.toFixed(2) || '0.00'}</p>
                </div>
                <Euro className="h-12 w-12 text-blue-200" />
              </div>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg border border-blue-200 p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-sm text-slate-600 mb-2 block">Summary Period</Label>
              <Select value={summaryPeriod} onValueChange={setSummaryPeriod}>
                <SelectTrigger className="h-11 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">By Day</SelectItem>
                  <SelectItem value="month">By Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-7">
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="h-11 bg-black hover:bg-slate-800 text-white rounded-sm"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Expense
              </Button>
            </div>
          </div>
        </div>

        {/* Period Breakdown */}
        {summary && summary.summaries && summary.summaries.length > 0 && (
          <div className="bg-white rounded-lg border border-blue-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-black mb-4">
              {summaryPeriod === 'day' ? 'Daily' : 'Monthly'} Breakdown
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Period</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Commission</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Expenses</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Net</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Rides</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Expense Count</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.summaries.map((item, index) => (
                    <tr key={item.period || index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.period}</td>
                      <td className="px-4 py-3 text-sm text-emerald-600 font-medium">€{item.total_commission?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-red-600 font-medium">€{item.total_expenses?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-blue-600">€{item.net?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.ride_count}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.expense_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Expenses List */}
        <div className="bg-white rounded-lg border border-blue-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Reason</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Receipt</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      Loading expenses...
                    </td>
                  </tr>
                ) : expenses.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No expenses found. Add your first expense!
                    </td>
                  </tr>
                ) : (
                  expenses.map(expense => (
                    <tr key={expense.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-bold text-red-600">
                        €{expense.amount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {formatDateTime(expense.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {expense.reason || '-'}
                      </td>
                      <td className="px-6 py-4">
                        {expense.receipt_data ? (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewReceipt(expense)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">No receipt</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditDialog(expense)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm"
                            title="Edit expense"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setExpenseToDelete(expense);
                              setDeleteDialogOpen(true);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-sm"
                            title="Delete expense"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                <div>
                  <Label htmlFor="amount" className="text-slate-700 font-medium mb-2 block">
                    Amount (€)
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="25.00"
                    className="h-11 rounded-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="date" className="text-slate-700 font-medium mb-2 block">
                    Date & Time
                  </Label>
                  <Input
                    id="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="h-11 rounded-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="reason" className="text-slate-700 font-medium mb-2 block">
                    Reason
                  </Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Fuel, tolls, parking, etc..."
                    className="rounded-sm min-h-[100px]"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    Receipt Photo (Proof)
                  </Label>
                  <div className="mt-1 space-y-3">
                    {/* Two options: Take Photo or Upload File */}
                    <div className="flex gap-2">
                      {/* Camera capture button */}
                      <div className="flex-1">
                        <Input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          capture="environment"
                          className="hidden"
                          id="camera-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-11 rounded-sm"
                        >
                          <Camera className="mr-2 h-5 w-5" />
                          Take Photo
                        </Button>
                      </div>
                      
                      {/* File upload button */}
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('file-upload-input')?.click()}
                          className="w-full h-11 rounded-sm"
                        >
                          <Upload className="mr-2 h-5 w-5" />
                          Upload File
                        </Button>
                      </div>
                    </div>
                  </div>
                  {formData.receipt_filename && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                      <FileImage className="h-4 w-4" />
                      {formData.receipt_filename}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Max 5MB. Take a photo with camera or upload an image/PDF of your receipt.</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Please upload a receipt photo as proof of your expense.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-black hover:bg-slate-800 text-white rounded-sm"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Expense</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this expense? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Receipt Modal */}
        <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Receipt</DialogTitle>
            </DialogHeader>
            {selectedReceipt && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img 
                    src={selectedReceipt} 
                    alt="Receipt" 
                    className="max-h-[60vh] object-contain rounded-lg"
                  />
                </div>
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedReceipt;
                      link.download = `receipt_${Date.now()}.jpg`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('Receipt downloaded');
                    }}
                    data-testid="download-receipt-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Receipt
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
